// npm library imports
import Link from 'next/link';

// style import
import styles from './linkComponent.module.scss';

// type import
import type { LinkProps } from '@lib/types';
import { useMemo } from 'react';

/**
 * Individual link item for link component.
 * Renders internal or external link based on URL.
 *
 * @param props - The component props object
 * @param props.text Text to display in the link
 * @param props.url URL to link to
 * @param props.onClick Optional - function to run on click
 * @param props.target Optional - _blank or _self in link. default _self
 * @returns Individual link item
 *
 * @example
 * ```tsx
 * <LinkItem text="Link" url="/link"/>
 * ```
 */
const LinkItem = ({ text, url, onClick, target = '_self' }: LinkProps) => {

    // Check if link is external or internal
    const isExternal: boolean = useMemo(() => {
        const externalUrlPattern = /^(https?:|ftp:|mailto:|tel:)/i;
        return externalUrlPattern.test(url);
    }, [url]);  

    // Render external or internal link
    return isExternal ? (
        <a
            href={url}
            onClick={onClick}
            target={target}
            className={styles.link}
            style={{ textDecoration: 'none' }}
        >
            {text}
        </a>
    ) : (
        <Link
            href={url}
            onClick={onClick}
            target={target}
            className={styles.link}
        >
            {text}
        </Link>
    );
};

export default LinkItem;
